import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, Output, QueryList } from '@angular/core';
import { FormGroup } from '@angular/forms';

/**
 * Step of an <app-stepper>. Replaces <mdb-step>: same inputs (name, stepForm) and
 * the same fields read by existing onStepChange handlers (name, isDone, stepForm).
 */
@Component({
    selector: 'app-step',
    template: `<div [hidden]="!isActive"><ng-content></ng-content></div>`,
    standalone: false
})
export class StepComponent {
  @Input() name: string;
  @Input() stepForm: FormGroup;
  isDone = false;
  isActive = false;
}

export interface StepChangeEvent {
  activeStep: StepComponent;
  activeStepIndex: number;
  previousStep: StepComponent;
  previousStepIndex: number;
}

/**
 * Replaces <mdb-stepper>: horizontal step headers, next()/previous()/resetAll(),
 * (stepChange) and [linear] (can't move past a step whose stepForm is invalid).
 */
@Component({
    selector: 'app-stepper',
    template: `
    <ol class="cs-stepper-head">
      <li *ngFor="let step of steps; let i = index" (click)="setNewActiveStep(i)"
          [class.active]="step.isActive" [class.done]="step.isDone">
        <span class="cs-stepper-num">{{ i + 1 }}</span><span class="cs-stepper-label">{{ step.name }}</span>
      </li>
    </ol>
    <div class="cs-stepper-body"><ng-content></ng-content></div>`,
    styles: [`
    .cs-stepper-head { display: flex; list-style: none; padding: 0; margin: 0 0 1rem; border-bottom: 1px solid #e0e0e0; }
    .cs-stepper-head li { flex: 1; display: flex; align-items: center; padding: .75rem .5rem; cursor: pointer; color: #757575; }
    .cs-stepper-num { width: 1.6rem; height: 1.6rem; border-radius: 50%; background: #9e9e9e; color: #fff; display: inline-flex; align-items: center; justify-content: center; margin-right: .5rem; font-size: .8rem; flex: none; }
    .cs-stepper-head li.active { color: #212121; font-weight: 500; }
    .cs-stepper-head li.active .cs-stepper-num, .cs-stepper-head li.done .cs-stepper-num { background: #4285f4; }
  `],
    standalone: false
})
export class StepperComponent implements AfterContentInit {
  @Input() linear = false;
  @Output() stepChange = new EventEmitter<StepChangeEvent>();
  @ContentChildren(StepComponent) stepList: QueryList<StepComponent>;

  activeStepIndex = 0;

  get steps(): StepComponent[] { return this.stepList ? this.stepList.toArray() : []; }
  get activeStep(): StepComponent { return this.steps[this.activeStepIndex]; }

  ngAfterContentInit() {
    // activate after the first check so the header bindings don't change mid-cycle
    Promise.resolve().then(() => this.activate(0, false));
  }

  next() { this.setNewActiveStep(this.activeStepIndex + 1); }
  previous() { this.setNewActiveStep(this.activeStepIndex - 1); }

  resetAll() {
    for (const s of this.steps) {
      s.isDone = false;
      if (s.stepForm) { s.stepForm.reset(); }
    }
    this.activate(0, true);
  }

  setNewActiveStep(index: number) {
    if (index < 0 || index >= this.steps.length || index === this.activeStepIndex) { return; }
    if (index > this.activeStepIndex) {
      // moving forward: in linear mode every step in between must be valid
      for (let i = this.activeStepIndex; i < index; i++) {
        const form = this.steps[i].stepForm;
        if (this.linear && form && form.invalid) {
          form.markAllAsTouched();
          return;
        }
        this.steps[i].isDone = true;
      }
    }
    this.activate(index, true);
  }

  private activate(index: number, emit: boolean) {
    const previousStepIndex = this.activeStepIndex;
    this.steps.forEach((s, i) => (s.isActive = i === index));
    this.activeStepIndex = index;
    if (emit) {
      this.stepChange.emit({
        activeStep: this.steps[index],
        activeStepIndex: index,
        previousStep: this.steps[previousStepIndex],
        previousStepIndex,
      });
    }
  }
}
